import { useState, useEffect, useCallback, useRef } from 'react';
import { LogEntry } from '../types';
import { DEBUG_CONFIG } from '../config';
import { loggerStore } from '../utils/loggerStore';

export const useLogger = () => {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [isPaused, setIsPaused] = useState(false);
  const logIdCounter = useRef(0);

  // Intercept console methods
  useEffect(() => {
    if (!DEBUG_CONFIG.enabled || isPaused) return;

    const originalLog = console.log;
    const originalWarn = console.warn;
    const originalError = console.error;
    const originalInfo = console.info;

    const createLogEntry = (level: LogEntry['level'], args: any[]): LogEntry => {
      const message = args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg, null, 2);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      }).join(' ');

      const stack = level === 'error' && args[0]?.stack ? args[0].stack : undefined;

      return {
        id: `log_${Date.now()}_${logIdCounter.current++}`,
        level,
        message,
        timestamp: Date.now(),
        data: args.length > 1 ? args.slice(1) : undefined,
        stack,
      };
    };

    console.log = (...args: any[]) => {
      if (!isPaused) {
        const entry = createLogEntry('log', args);
        loggerStore.addLog(entry);
        setLogs((prev) => {
          const newLogs = [entry, ...prev];
          return newLogs.slice(0, DEBUG_CONFIG.maxConsoleLogs);
        });
      }
      originalLog.apply(console, args);
    };

    console.warn = (...args: any[]) => {
      if (!isPaused) {
        const entry = createLogEntry('warn', args);
        loggerStore.addLog(entry);
        setLogs((prev) => {
          const newLogs = [entry, ...prev];
          return newLogs.slice(0, DEBUG_CONFIG.maxConsoleLogs);
        });
      }
      originalWarn.apply(console, args);
    };

    console.error = (...args: any[]) => {
      if (!isPaused) {
        const entry = createLogEntry('error', args);
        loggerStore.addLog(entry);
        setLogs((prev) => {
          const newLogs = [entry, ...prev];
          return newLogs.slice(0, DEBUG_CONFIG.maxConsoleLogs);
        });
      }
      originalError.apply(console, args);
    };

    console.info = (...args: any[]) => {
      if (!isPaused) {
        const entry = createLogEntry('info', args);
        loggerStore.addLog(entry);
        setLogs((prev) => {
          const newLogs = [entry, ...prev];
          return newLogs.slice(0, DEBUG_CONFIG.maxConsoleLogs);
        });
      }
      originalInfo.apply(console, args);
    };

    return () => {
      console.log = originalLog;
      console.warn = originalWarn;
      console.error = originalError;
      console.info = originalInfo;
    };
  }, [isPaused]);

  const clearLogs = useCallback(() => {
    loggerStore.clearLogs();
    setLogs([]);
  }, []);

  const exportLogs = useCallback(() => {
    try {
      const logText = logs.map(log => {
        const timestamp = new Date(log.timestamp).toISOString();
        const level = log.level.toUpperCase().padEnd(5);
        return `[${timestamp}] ${level} ${log.message}${log.stack ? `\n${log.stack}` : ''}`;
      }).join('\n\n');

      const blob = new Blob([logText], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `debug-logs-${Date.now()}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting logs:', error);
    }
  }, [logs]);

  const copyLog = useCallback((log: LogEntry) => {
    try {
      const logText = `${log.message}${log.data ? `\n${JSON.stringify(log.data, null, 2)}` : ''}${log.stack ? `\n${log.stack}` : ''}`;
      navigator.clipboard.writeText(logText);
    } catch (error) {
      console.error('Error copying log:', error);
    }
  }, []);

  return {
    logs,
    isPaused,
    setIsPaused,
    clearLogs,
    exportLogs,
    copyLog,
  };
};

